import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  /** Icon before the label. */
  icon?: IconName;
  /** Icon after the label. */
  iconAfter?: IconName;
  block?: boolean;
  /** Shows a spinner and blocks repeat taps. */
  loading?: boolean;
  children?: ReactNode;
}

/** Text button. Always at least 44 x 44 px. Primary is amber; use one primary per view. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconAfter, block, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('btn', `btn--${variant}`, size !== 'md' && `btn--${size}`, block && 'btn--block', loading && 'btn--loading', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner btn__spinner" aria-hidden /> : icon ? <Icon name={icon} size={size === 'sm' ? 18 : 20} /> : null}
      {children !== undefined && <span className="btn__label">{children}</span>}
      {iconAfter && !loading && <Icon name={iconAfter} size={size === 'sm' ? 18 : 20} />}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** Required accessible name (also shown as the tooltip title). */
  label: string;
  filled?: boolean;
  size?: number;
  /** For toggle buttons. */
  pressed?: boolean;
}

/** Square icon-only button, 44 x 44 px, with a required accessible label. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, filled, size = 22, pressed, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('icon-btn', filled && 'icon-btn--filled', className)}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      {...rest}
    >
      <Icon name={icon} size={size} />
    </button>
  );
});
